import {
  BarChart2,
  TrendingUp,
  Target,
  Landmark,
  Activity,
  Filter,
  Zap,
  TrendingDown,
  Layers,
  AlertTriangle,
  ArrowUpDown,
  Calendar,
  Percent,
  DollarSign,
  Clock,
  Volume2,
  LineChart,
  CandlestickChart,
  RefreshCcw,
  CheckCircle2,
  Grid3X3,
  Tag,
} from 'lucide-react';
import type { FilterItem, FilterCategoryItem, DefaultValuesMap, FilterOption } from './types';

// Helper to create default values map from filter config
export const createDefaultValuesMap = (filters: FilterItem[]): DefaultValuesMap => {
  const map: DefaultValuesMap = {};
  filters.forEach((filter) => {
    if (filter.children) {
      // For toggle-with-detail, include children defaults
      filter.children.forEach((child) => {
        map[child.id] = child.defaultValue;
      });
      // Include config defaults if present
      if (filter.configDefaults) {
        Object.entries(filter.configDefaults).forEach(([key, value]) => {
          map[`${filter.configKey}.${key}`] = value;
        });
      }
    }
    map[filter.id] = filter.defaultValue;
    // Use filter's defaultValue for enabled state (e.g. MA Bounce Reversal default ON, Strong MA Support default ON)
    if (filter.enabledKey) {
      map[filter.enabledKey] = filter.defaultValue;
    }
  });
  return map;
};

// Market Criteria Filters
export const marketCriteriaFilters: FilterItem[] = [
  {
    id: 'min_market_cap',
    type: 'number',
    label: 'Minimum Market Cap',
    shortDescription: 'Minimum market capitalization',
    description: 'Only show stocks with market cap above this threshold. Larger caps typically offer more stability.',
    defaultValue: 500000000,
    min: 0,
    max: 10000000000000,
    step: 100000000,
    prefix: '$',
    suffix: '',
    icon: DollarSign,
  },
  {
    id: 'min_price_target_percent',
    type: 'number',
    label: 'Min Price Target %',
    shortDescription: 'Minimum analyst price target upside',
    description: 'Only show stocks where the 12-month price target is at least this percentage above current price.',
    defaultValue: 0.10,
    min: 0,
    max: 1,
    step: 0.01,
    prefix: '',
    suffix: '%',
    unit: 'percent',
    icon: Target,
  },
  {
    id: 'price_target_mode',
    type: 'select',
    label: 'Price Target Mode (Experimental)',
    shortDescription: 'How to calculate price targets',
    description: 'Existing: Uses analyst price targets from financial data providers. Significant High: Calculates targets based on recent significant highs (4-month lookback).',
    defaultValue: 'significant_high',
    options: [
      { value: 'existing', label: 'Existing (Analyst Targets)', subtitle: 'Use analyst price targets from financial data providers' },
      { value: 'significant_high', label: 'Significant High (Technical)', subtitle: 'Calculates targets based on recent significant highs (4-month lookback)' },
    ],
    icon: Target,
  },
  {
    id: 'instrument_type_filter',
    type: 'segmented',
    label: 'Instrument Type',
    shortDescription: 'Filter by instrument type',
    description: 'Filter the stock universe by instrument type. Choose to include all instruments, only individual stocks, or only ETFs.',
    defaultValue: 'ALL',
    options: [
      { value: 'ALL', label: 'All', subtitle: 'Stocks + ETFs' },
      { value: 'STOCK', label: 'Stocks Only', subtitle: 'Exclude ETFs' },
      { value: 'ETF', label: 'ETFs Only', subtitle: 'Exclude individual stocks' },
    ],
    icon: Tag,
  },
  {
    id: 'index_filters',
    type: 'card-checkbox-group',
    label: 'Market Index Filters',
    shortDescription: 'Restrict screening to specific indices',
    description: 'Restrict screening to stocks from specific market indices. Leave all unchecked to include all stocks. If no indices are selected, all stocks are included.',
    defaultValue: [],
    hasSelectAll: true,
    options: [
      { value: 'sp500', label: 'S&P 500', subtitle: 'Large-cap U.S. stocks representing 500 leading companies', icon: BarChart2 },
      { value: 'nasdaq100', label: 'NASDAQ 100', subtitle: 'Top 100 non-financial companies listed on NASDAQ', icon: TrendingUp },
      { value: 'russell2000', label: 'Russell 2000', subtitle: 'Small-cap U.S. stocks representing emerging companies', icon: Layers },
      { value: 'djia', label: 'DJIA', subtitle: 'Dow Jones Industrial Average - 30 blue-chip stocks', icon: Activity },
    ],
    icon: Grid3X3,
  },
];

// MA Criteria Filters
export const maCriteriaFilters: FilterItem[] = [
  {
    id: 'ma_filter_selection',
    type: 'card-checkbox-group',
    label: 'Moving Average Selection',
    shortDescription: 'Which MAs to evaluate',
    description: 'Select which moving averages to use for proximity analysis. Select "No MA Filter" for permissive mode (no MA proximity requirement). You can select multiple moving averages. Stocks must be near at least ONE selected MA.',
    defaultValue: ['MA_150'],
    hasSelectAll: true,
    options: [
      { value: 'MA_5', label: 'MA 5', subtitle: 'Scalping / ultra short-term', badge: 'Aggressive' },
      { value: 'MA_10', label: 'MA 10', subtitle: 'Short-term swing', badge: 'Aggressive' },
      { value: 'MA_20', label: 'MA 20', subtitle: 'Short-term trend', badge: 'Aggressive' },
      { value: 'MA_50', label: 'MA 50', subtitle: 'Short-term momentum', badge: 'Aggressive' },
      { value: 'MA_100', label: 'MA 100', subtitle: 'Short-term intermediate (3-5 month trend)', badge: 'Aggressive' },
      { value: 'MA_150', label: 'MA 150', subtitle: 'Medium-term trend', badge: 'Moderate' },
      { value: 'MA_200', label: 'MA 200', subtitle: 'Long-term trend', badge: 'Recommended' },
      { value: 'NONE', label: 'No MA Filter', subtitle: 'Permissive: No MA requirements' },
    ],
    icon: LineChart,
  },
  {
    id: 'ma_timeframe_mode',
    type: 'segmented',
    label: 'MA Evaluation Timeframe',
    shortDescription: 'Daily or weekly MA evaluation',
    description: 'Daily (Swing): standard daily MAs. Weekly (Position/Trend): uses 50/100/150/200-week MAs (much longer-term than daily MAs).',
    defaultValue: 'DAILY',
    options: [
      { value: 'DAILY', label: 'Daily (Swing)' },
      { value: 'WEEKLY', label: 'Weekly (Pos/Trend)' },
    ],
    icon: Calendar,
  },
  {
    id: 'ma_proximity_min',
    type: 'number',
    label: 'MA Proximity Min',
    shortDescription: 'Minimum distance from MA',
    description: 'Current price must be at least this percentage below the moving average (negative = below).',
    defaultValue: -0.02,
    min: -0.5,
    max: 0,
    step: 0.005,
    prefix: '',
    suffix: '%',
    unit: 'percent',
    icon: TrendingDown,
  },
  {
    id: 'ma_proximity_max',
    type: 'number',
    label: 'MA Proximity Max',
    shortDescription: 'Maximum distance from MA',
    description: 'Current price must be at most this percentage above the moving average.',
    defaultValue: 0.045,
    min: 0,
    max: 0.5,
    step: 0.005,
    prefix: '',
    suffix: '%',
    unit: 'percent',
    icon: TrendingUp,
  },
  {
    id: 'require_recent_ma150_cross',
    type: 'toggle',
    label: 'Require recent MA150 cross',
    shortDescription: 'Reject MA150-proximity hits with no cross in recent window',
    description:
      'When enabled, a ticker must have at least one MA150 close-cross in the last N candles to pass the MA150 proximity filter. Fails open on insufficient history.',
    defaultValue: true,
    icon: TrendingUp,
  },
  {
    id: 'recent_ma150_cross_lookback_days',
    type: 'number',
    label: 'MA150 cross lookback',
    shortDescription: 'Candles to scan for a recent MA150 cross',
    description:
      'Number of recent candles (daily bars in daily mode, weekly bars in weekly mode) to scan for at least one MA150 cross.',
    defaultValue: 6,
    min: 1,
    max: 50,
    step: 1,
    suffix: ' candles',
    icon: TrendingUp,
  },
  {
    id: 'recent_ma150_cross_bypass_pct',
    type: 'number',
    label: 'MA150 near-touch bypass',
    shortDescription: 'Skip cross check when within this % of MA150',
    description:
      'If the last candle is within this percent of MA150 (absolute distance), the cross-history requirement is waived. Treats a live near-touch as equivalent to an actual cross. Set to 0 to always require a historical cross. Capped at 5% so the bypass stays narrower than the proximity band.',
    defaultValue: 0.02,
    min: 0,
    max: 0.05,
    step: 0.005,
    suffix: '%',
    unit: 'percent',
    icon: TrendingUp,
  },
  {
    id: 'trend_period_months',
    type: 'number',
    label: 'Trend Period',
    shortDescription: 'Months for trend analysis',
    description: 'Number of months to analyze for trend confirmation.',
    defaultValue: 6,
    min: 1,
    max: 24,
    step: 1,
    suffix: ' mo',
    icon: Calendar,
  },
];

// Volume & Momentum Filters (Finviz-style sh_avgvol / sh_relvol / sh_curvol)
const SH_AVGVOL_OPTIONS: FilterOption[] = [
  { value: '', label: 'Any' },
  { value: 'u50', label: 'Under 50K' },
  { value: 'u100', label: 'Under 100K' },
  { value: 'u500', label: 'Under 500K' },
  { value: 'u750', label: 'Under 750K' },
  { value: 'u1000', label: 'Under 1M' },
  { value: 'o50', label: 'Over 50K' },
  { value: 'o100', label: 'Over 100K' },
  { value: 'o200', label: 'Over 200K' },
  { value: 'o300', label: 'Over 300K' },
  { value: 'o400', label: 'Over 400K' },
  { value: 'o500', label: 'Over 500K' },
  { value: 'o750', label: 'Over 750K' },
  { value: 'o1000', label: 'Over 1M' },
  { value: 'o2000', label: 'Over 2M' },
  { value: '100to500', label: '100K to 500K' },
  { value: '100to1000', label: '100K to 1M' },
  { value: '500to1000', label: '500K to 1M' },
  { value: '500to10000', label: '500K to 10M' },
];

const SH_RELVOL_OPTIONS: FilterOption[] = [
  { value: '', label: 'Any' },
  { value: 'o10', label: 'Over 10' },
  { value: 'o5', label: 'Over 5' },
  { value: 'o3', label: 'Over 3' },
  { value: 'o2', label: 'Over 2' },
  { value: 'o1.5', label: 'Over 1.5' },
  { value: 'o1', label: 'Over 1' },
  { value: 'o0.75', label: 'Over 0.75' },
  { value: 'o0.5', label: 'Over 0.5' },
  { value: 'o0.25', label: 'Over 0.25' },
  { value: 'u2', label: 'Under 2' },
  { value: 'u1.5', label: 'Under 1.5' },
  { value: 'u1', label: 'Under 1' },
  { value: 'u0.75', label: 'Under 0.75' },
  { value: 'u0.5', label: 'Under 0.5' },
  { value: 'u0.25', label: 'Under 0.25' },
  { value: 'u0.1', label: 'Under 0.1' },
];

const SH_CURVOL_OPTIONS: FilterOption[] = [
  { value: '', label: 'Any' },
  { value: 'u50', label: 'Under 50K' },
  { value: 'u100', label: 'Under 100K' },
  { value: 'u500', label: 'Under 500K' },
  { value: 'u750', label: 'Under 750K' },
  { value: 'u1000', label: 'Under 1M' },
  { value: 'o0', label: 'Over 0' },
  { value: 'o50', label: 'Over 50K' },
  { value: 'o100', label: 'Over 100K' },
  { value: 'o200', label: 'Over 200K' },
  { value: 'o300', label: 'Over 300K' },
  { value: 'o400', label: 'Over 400K' },
  { value: 'o500', label: 'Over 500K' },
  { value: 'o750', label: 'Over 750K' },
  { value: 'o1000', label: 'Over 1M' },
  { value: 'o2000', label: 'Over 2M' },
  { value: 'o5000', label: 'Over 5M' },
  { value: 'o10000', label: 'Over 10M' },
  { value: 'o20000', label: 'Over 20M' },
];

export const volumeMomentumFilters: FilterItem[] = [
  {
    id: 'sh_avgvol',
    type: 'select',
    label: 'Average Volume',
    shortDescription: '20-day avg share volume',
    description: 'Filter by 20-day average share volume.',
    defaultValue: '',
    options: SH_AVGVOL_OPTIONS,
    icon: Volume2,
  },
  {
    id: 'sh_relvol',
    type: 'select',
    label: 'Relative Volume',
    shortDescription: 'Current volume / 20-day avg',
    description: 'Filter by current volume relative to the 20-day average.',
    defaultValue: '',
    options: SH_RELVOL_OPTIONS,
    icon: Activity,
  },
  {
    id: 'sh_curvol',
    type: 'select',
    label: 'Current Volume',
    shortDescription: "Today's share volume",
    description: "Filter by today's share volume.",
    defaultValue: '',
    options: SH_CURVOL_OPTIONS,
    icon: BarChart2,
  },
];

// Pattern Filters
export const patternFilters: FilterItem[] = [
  {
    id: 'standard_screening_enabled',
    type: 'toggle',
    label: 'Use Pattern Shortcut',
    shortDescription: 'Use pattern filters as a shortcut',
    description: 'When enabled, uses pattern filters as a shortcut for screening (uses pattern-based filtering instead of strict exclusive patterns). Disable to use strict exclusive pattern matching.',
    defaultValue: false,
    icon: Filter,
  },
  // NEW: Enhanced Trend Configuration
  // NOTE: Hidden until OR logic bug is fixed - kept in config for future use
  {
    id: 'bullish_momentum_enabled',
    type: 'toggle-with-detail',
    label: 'Trend Filters',
    shortDescription: 'Enhanced bullish/bearish momentum',
    description: 'Enable enhanced trend filters using multi-factor analysis (MA relationships, slopes, momentum indicators). When disabled, skips trend checks.',
    defaultValue: false,
    uiHidden: true,  // Hidden until bug is fixed
    icon: TrendingUp,
    children: [
      {
        id: 'trend_direction',
        type: 'select',
        label: 'Trend Direction',
        shortDescription: 'bullish, bearish, neutral, any',
        description: 'Filter by trend direction: Bullish (price above MA200 with positive slope), Bearish (price below MA200), Neutral (price near MA200), or Any.',
        defaultValue: 'any',
        options: [
          { value: 'any', label: 'Any', subtitle: 'No direction filter' },
          { value: 'bullish', label: 'Bullish', subtitle: 'Price above MA200, positive slope' },
          { value: 'bearish', label: 'Bearish', subtitle: 'Price below MA200, negative slope' },
          { value: 'neutral', label: 'Neutral', subtitle: 'Price within 5% of MA200' },
        ],
      },
      {
        id: 'trend_strength',
        type: 'select',
        label: 'Trend Strength',
        shortDescription: 'strong, normal, any',
        description: 'Filter by trend strength: Strong (all MAs in order: MA50 > MA150 > MA200), Normal (basic bullish/bearish criteria), or Any.',
        defaultValue: 'any',
        options: [
          { value: 'any', label: 'Any', subtitle: 'No strength filter' },
          { value: 'strong', label: 'Strong', subtitle: 'All MAs in bullish order' },
          { value: 'normal', label: 'Normal', subtitle: 'Basic bullish/bearish' },
        ],
      },
      {
        id: 'momentum_confirmation',
        type: 'select',
        label: 'Momentum Confirmation',
        shortDescription: 'RSI, Stochastic, Any, None',
        description: 'Require momentum indicator confirmation: RSI >= 50, Stochastic %K > 50, or Any. None skips momentum check.',
        defaultValue: 'any',
        options: [
          { value: 'any', label: 'Any', subtitle: 'RSI or Stochastic OK' },
          { value: 'rsi', label: 'RSI', subtitle: 'RSI >= 50 required' },
          { value: 'stochastic', label: 'Stochastic', subtitle: 'Stochastic %K > 50 required' },
          { value: 'none', label: 'None', subtitle: 'No momentum check' },
        ],
      },
    ],
  },
  // NEW: Bullish Transition Filter with Settings
  // NOTE: Hidden until OR logic bug is fixed
  {
    id: 'bullish_transition_enabled',
    type: 'toggle-with-detail',
    label: 'Bullish Transition',
    shortDescription: 'Bearish to Bullish shift',
    description: 'Detect stocks transitioning from bearish to bullish (recent breakout above MA200 after being below). Uses 60-day lookback.',
    defaultValue: false,
    uiHidden: true,  // Hidden until bug is fixed
    icon: TrendingUp,
    children: [
      {
        id: 'transition_lookback_days',
        type: 'select',
        label: 'Lookback Days',
        shortDescription: 'Days to look back',
        description: 'Number of days to look back to determine if stock was previously bearish (default: 60).',
        defaultValue: '60',
        options: [
          { value: '30', label: '30 days', subtitle: 'Shorter lookback' },
          { value: '45', label: '45 days', subtitle: 'Medium lookback' },
          { value: '60', label: '60 days', subtitle: 'Default lookback' },
          { value: '90', label: '90 days', subtitle: 'Longer lookback' },
        ],
      },
      {
        id: 'transition_require_momentum',
        type: 'toggle',
        label: 'Require Momentum',
        shortDescription: 'RSI or Stochastic',
        description: 'Require RSI >= 50 or Stochastic %K > 50 for transition confirmation.',
        defaultValue: true,
      },
      {
        id: 'transition_require_volume',
        type: 'toggle',
        label: 'Require Volume Spike',
        shortDescription: '1.5x average volume',
        description: 'Require breakout day volume to be at least 1.5x the 20-day average volume.',
        defaultValue: false,
      },
    ],
  },
  // NEW: Bearish Transition Filter with Settings
  // NOTE: Hidden until OR logic bug is fixed
  {
    id: 'bearish_transition_enabled',
    type: 'toggle-with-detail',
    label: 'Bearish Transition',
    shortDescription: 'Bullish to Bearish shift',
    description: 'Detect stocks transitioning from bullish to bearish (recent breakdown below MA200 after being above). Uses 60-day lookback.',
    defaultValue: false,
    uiHidden: true,  // Hidden until bug is fixed
    icon: TrendingDown,
    children: [
      {
        id: 'transition_lookback_days_bearish',
        type: 'select',
        label: 'Lookback Days',
        shortDescription: 'Days to look back',
        description: 'Number of days to look back to determine if stock was previously bullish (default: 60).',
        defaultValue: '60',
        options: [
          { value: '30', label: '30 days', subtitle: 'Shorter lookback' },
          { value: '45', label: '45 days', subtitle: 'Medium lookback' },
          { value: '60', label: '60 days', subtitle: 'Default lookback' },
          { value: '90', label: '90 days', subtitle: 'Longer lookback' },
        ],
      },
      {
        id: 'transition_require_momentum_bearish',
        type: 'toggle',
        label: 'Require Momentum',
        shortDescription: 'RSI or Stochastic',
        description: 'Require RSI < 50 or Stochastic %K < 50 for transition confirmation.',
        defaultValue: true,
      },
      {
        id: 'transition_require_volume_bearish',
        type: 'toggle',
        label: 'Require Volume Spike',
        shortDescription: '1.5x average volume',
        description: 'Require breakdown day volume to be at least 1.5x the 20-day average volume.',
        defaultValue: false,
      },
    ],
  },
  {
    id: 'doji_filter_enabled',
    type: 'toggle',
    label: 'Reversal Doji on Support',
    shortDescription: 'Doji within 1.5% of MA50/MA150/MA200 support',
    description: 'A single doji candle (open ≈ close) printed within 1.5% of a major moving average - signals indecision at support and a possible reversal entry.',
    defaultValue: false,
    icon: CandlestickChart,
  },
  {
    id: 'cup_handle_filter_enabled',
    type: 'toggle',
    label: 'Cup and Handle Pattern',
    shortDescription: 'U-shaped base + shallow handle pullback',
    description: 'Detects a multi-week rounded base (the cup) followed by a shallow pullback (the handle). Bullish continuation; ideal entry is on the handle breakout.',
    defaultValue: false,
    icon: TrendingUp,
  },
  {
    id: 'head_shoulders_filter_enabled',
    type: 'toggle',
    label: 'Head and Shoulders',
    shortDescription: 'Three-peak topping pattern; bearish',
    description: 'Three peaks where the middle (head) sits above the two shoulders, with a neckline drawn through the troughs. Confirmed by a close below the neckline - bearish reversal.',
    defaultValue: false,
    icon: Landmark,
  },
  {
    id: 'double_pattern_filter_enabled',
    type: 'toggle',
    label: 'Double Top/Bottom',
    shortDescription: 'Twin highs (top) or twin lows (bottom)',
    description: 'Two roughly equal highs (double top - bearish) or two roughly equal lows (double bottom - bullish) separated by a pullback. Confirmation comes from the trough/peak break.',
    defaultValue: false,
    icon: Layers,
  },
  {
    id: 'breakout_filter_enabled',
    type: 'toggle',
    label: 'Range Breakout (pattern)',
    shortDescription: 'Breakout from a tight consolidation',
    description: 'Price closes above the high of a multi-week consolidation range (rectangle/triangle base). For trend-line / N-day-high breakouts with volume gating, see Extended → Breakout Filter.',
    defaultValue: false,
    icon: Zap,
  },
  {
    id: 'green_doji_filter_enabled',
    type: 'toggle',
    label: 'Green Doji Pattern',
    shortDescription: 'Doji that closed slightly green',
    description: 'Doji-shaped candle whose close is fractionally above the open, suggesting buyer absorption at the day\'s low - bullish bias when it appears at support.',
    defaultValue: false,
    icon: CandlestickChart,
  },
  {
    id: 'strong_red_reversal_filter_enabled',
    type: 'toggle',
    label: 'Strong Red Candles Reversal',
    shortDescription: 'Reversal from red days',
    description: 'Find strong red candles (>10% drop) followed by green reversal.',
    defaultValue: false,
    icon: RefreshCcw,
  },
  {
    id: 'pullback_to_support_filter_enabled',
    type: 'toggle',
    label: 'Pullback to Support',
    shortDescription: 'Retest of MA / prior swing low with bounce',
    description: 'Stock retraces from a recent high back to a tested support level (moving average or prior swing low) and prints a bounce candle - entry on dip-buying confirmation.',
    defaultValue: false,
    icon: TrendingDown,
  },
  // Strong MA Support Filter - added to Patterns section
  {
    id: 'strong_ma_support_enabled',
    type: 'toggle-with-detail',
    label: 'Strong MA Support',
    shortDescription: 'Reliable MA support and bounces',
    description: 'Stocks with reliable moving average support and volume-confirmed bounces.',
    defaultValue: false,
    enabledKey: 'strong_ma_support_config.enabled',
    detailEnabledKey: 'strong_ma_support_configExpanded',
    configKey: 'strong_ma_support_config',
    configDefaults: {
      enabled: false,
      moving_averages: ['MA_100', 'MA_150'],
      touch_tolerance_pct: 1.5,
      min_rebound_pct: 2.0,
      min_volume_multiplier: 1.5,
      lookback_days: 30,
      required_touches: 2,
      ma_slope_window: 5,
      min_ma_slope_pct: 0.0,
      require_higher_lows: true,
      require_rsi_confirmation: false,
      rsi_entry_min: 35,
      rsi_entry_max: 50,
      rsi_exit_min: 50,
      rsi_confirmation_window: 5,
      ma_confluence_window: 3,
    },
    icon: LineChart,
  },
  {
    id: 'gap_down_filter_enabled',
    type: 'toggle-with-detail',
    label: 'Gap-Down Recovery',
    shortDescription: 'Unfilled gap-downs',
    description: 'Find stocks with significant unfilled gap-downs for potential recovery plays.',
    defaultValue: false,
    enabledKey: 'gap_down_filter_enabled',
    detailEnabledKey: 'gap_down_filter_configExpanded',
    configKey: 'gap_down_filter',
    configDefaults: {
      enabled: false,
      min_gap_percent: 6.0,
      require_true_gap: false,
      lookback_days: 5,
      unfilled_only: false,
    },
    icon: ArrowUpDown,
    children: [
      {
        id: 'gap_down_filter.min_gap_percent',
        type: 'number',
        label: 'Min Gap %',
        defaultValue: 6.0,
        min: 1,
        max: 20,
        step: 0.5,
        suffix: '%',
      },
      {
        id: 'gap_down_filter.require_true_gap',
        type: 'toggle',
        label: 'Require True Gap',
        defaultValue: false,
      },
      {
        id: 'gap_down_filter.lookback_days',
        type: 'number',
        label: 'Lookback Days',
        defaultValue: 5,
        min: 1,
        max: 30,
        step: 1,
      },
      {
        id: 'gap_down_filter.unfilled_only',
        type: 'toggle',
        label: 'Unfilled Only',
        defaultValue: false,
      },
    ],
  },
];

// Advanced Filters
export const advancedFilters: FilterItem[] = [
  {
    id: 'goldenCrossFilterEnabled',
    type: 'toggle',
    label: 'Golden Cross',
    shortDescription: 'MA crossover signal',
    description: 'Filter for stocks with golden cross (50 MA crossing above 200 MA).',
    defaultValue: false,
    icon: TrendingUp,
  },
  {
    id: 'fib_golden_zone_filter_enabled',
    type: 'toggle-with-detail',
    label: 'Fibonacci Golden Zone',
    shortDescription: 'Golden zone retracement',
    description: 'Find stocks retracing to the golden zone (50-61.8% Fibonacci retracement) with bullish confirmation.',
    defaultValue: false,
    enabledKey: 'fib_golden_zone_filter_enabled',
    detailEnabledKey: 'fib_golden_zone_configExpanded',
    configKey: 'fib_golden_zone_config',
    configDefaults: {
      lookback_days: 60,
      min_swing_move_pct: 15.0,
      volume_multiplier: 1.3,
      rsi_min: 35,
      rsi_max: 55,
    },
    icon: Target,
    children: [
      {
        id: 'fib_golden_zone_config.lookback_days',
        type: 'number',
        label: 'Lookback Days',
        defaultValue: 60,
        min: 20,
        max: 200,
        step: 5,
        icon: Calendar,
      },
      {
        id: 'fib_golden_zone_config.min_swing_move_pct',
        type: 'number',
        label: 'Min Swing Move %',
        defaultValue: 15.0,
        min: 5,
        max: 50,
        step: 1,
        suffix: '%',
        icon: TrendingUp,
      },
      {
        id: 'fib_golden_zone_config.volume_multiplier',
        type: 'number',
        label: 'Volume Multiplier',
        defaultValue: 1.3,
        min: 1.0,
        max: 3.0,
        step: 0.1,
        prefix: '',
        suffix: 'x',
        icon: Volume2,
      },
      {
        id: 'fib_golden_zone_config.rsi_min',
        type: 'number',
        label: 'RSI Min',
        defaultValue: 35,
        min: 20,
        max: 50,
        step: 1,
        icon: Activity,
      },
      {
        id: 'fib_golden_zone_config.rsi_max',
        type: 'number',
        label: 'RSI Max',
        defaultValue: 55,
        min: 40,
        max: 70,
        step: 1,
        icon: Activity,
      },
    ],
  },
  {
    id: 'volume_structure_enabled',
    type: 'toggle',
    label: 'Volume Structure Confluence',
    shortDescription: 'Boost stocks whose MA150 sits on a real volume shelf',
    description: 'Adds a deterministic Anchored Volume Profile signal (high/low volume nodes + point of control) to scoring: stocks where the 150-day MA overlaps a volume-confirmed support shelf, with room to the nearest volume resistance, rank higher. Does not exclude any stock - it only adjusts ranking. Off by default; turn off if results look worse.',
    defaultValue: false,
    icon: Volume2,
  },
  {
    id: 'darvas_box_filter_enabled',
    type: 'toggle-with-detail',
    label: 'Darvas Box',
    shortDescription: 'Tight consolidation box',
    description: 'Find stocks consolidating in a tight, flat-ceilinged/flat-floored box after a prior up-impulse, ranked by box quality and breakout readiness. Works best with the MA filter disabled.',
    defaultValue: false,
    enabledKey: 'darvas_box_filter_enabled',
    detailEnabledKey: 'darvas_box_configExpanded',
    configKey: 'darvas_box_config',
    configDefaults: {
      lookback_days: 250,
      min_impulse_pct: 15.0,
      max_box_atr: 2.0,
      min_touches: 2,
      containment_min_pct: 80,
    },
    icon: Layers,
    children: [
      {
        id: 'darvas_box_config.lookback_days',
        type: 'number',
        label: 'Lookback Days',
        defaultValue: 250,
        min: 60,
        max: 3650,
        step: 10,
        icon: Calendar,
      },
      {
        id: 'darvas_box_config.min_impulse_pct',
        type: 'number',
        label: 'Min Impulse %',
        defaultValue: 15.0,
        min: 1,
        max: 200,
        step: 1,
        suffix: '%',
        icon: TrendingUp,
      },
      {
        id: 'darvas_box_config.max_box_atr',
        type: 'number',
        label: 'Max Box Height (ATR)',
        defaultValue: 2.0,
        min: 0.2,
        max: 10.0,
        step: 0.1,
        suffix: 'x',
        icon: ArrowUpDown,
      },
      {
        id: 'darvas_box_config.min_touches',
        type: 'number',
        label: 'Min Boundary Touches',
        defaultValue: 2,
        min: 1,
        max: 10,
        step: 1,
        icon: CheckCircle2,
      },
      {
        id: 'darvas_box_config.containment_min_pct',
        type: 'number',
        label: 'Min Containment %',
        defaultValue: 80,
        min: 0,
        max: 100,
        step: 1,
        suffix: '%',
        icon: Percent,
      },
    ],
  },
  {
    id: 'strong_support_filter_enabled',
    type: 'toggle-with-detail',
    label: 'Strong Support Line',
    shortDescription: 'Horizontal/ascending support',
    description: 'Identify horizontal and ascending support lines with bullish reclaim and momentum kickoff.',
    defaultValue: false,
    enabledKey: 'strong_support_filter_enabled',
    detailEnabledKey: 'strong_support_configExpanded',
    configKey: 'strong_support_config',
    configDefaults: {
      lookback_days: 200,
      price_tolerance_pct: 0.5,
      min_touches: 3,
      min_bounce_pct: 2.0,
      min_spacing_days: 5,
      volume_multiplier: 1.3,
      slope_min: 0.01,
      slope_max: 2.0,
      stop_loss_buffer_pct: 1.0,
      reclaim_window_bars: 5,
      reclaim_vol_mult: 1.3,
      reclaim_vol_mult_max: 1.5,
      base_lookahead_bars: 7,
      kickoff_vol_mult: 1.3,
    },
    icon: Landmark,
    strategyKey: 'strong_support_strategy',
    strategyOptions: ['conservative', 'balanced', 'aggressive', 'custom'],
    strategyPresets: {
      conservative: { lookback_days: 200, price_tolerance_pct: 0.3, min_touches: 4, min_bounce_pct: 3.0, min_spacing_days: 7, volume_multiplier: 1.5, slope_min: 0.02, slope_max: 1.5, stop_loss_buffer_pct: 0.5, reclaim_window_bars: 7, reclaim_vol_mult: 1.5, reclaim_vol_mult_max: 1.8, base_lookahead_bars: 10, kickoff_vol_mult: 1.5 },
      balanced: { lookback_days: 200, price_tolerance_pct: 0.5, min_touches: 3, min_bounce_pct: 2.0, min_spacing_days: 5, volume_multiplier: 1.3, slope_min: 0.01, slope_max: 2.0, stop_loss_buffer_pct: 1.0, reclaim_window_bars: 5, reclaim_vol_mult: 1.3, reclaim_vol_mult_max: 1.5, base_lookahead_bars: 7, kickoff_vol_mult: 1.3 },
      aggressive: { lookback_days: 150, price_tolerance_pct: 0.8, min_touches: 2, min_bounce_pct: 1.0, min_spacing_days: 3, volume_multiplier: 1.0, slope_min: 0.0, slope_max: 3.0, stop_loss_buffer_pct: 1.5, reclaim_window_bars: 3, reclaim_vol_mult: 1.0, reclaim_vol_mult_max: 1.2, base_lookahead_bars: 5, kickoff_vol_mult: 1.0 },
      custom: { lookback_days: 200, price_tolerance_pct: 0.5, min_touches: 3, min_bounce_pct: 2.0, min_spacing_days: 5, volume_multiplier: 1.3, slope_min: 0.01, slope_max: 2.0, stop_loss_buffer_pct: 1.0, reclaim_window_bars: 5, reclaim_vol_mult: 1.3, reclaim_vol_mult_max: 1.5, base_lookahead_bars: 7, kickoff_vol_mult: 1.3 },
    },
    children: [
      {
        id: 'strong_support_config.lookback_days',
        type: 'number',
        label: 'Lookback Days',
        defaultValue: 200,
        min: 50,
        max: 500,
        step: 10,
        icon: Calendar,
      },
      {
        id: 'strong_support_config.price_tolerance_pct',
        type: 'number',
        label: 'Price Tolerance %',
        defaultValue: 0.5,
        min: 0.1,
        max: 5,
        step: 0.1,
        suffix: '%',
        icon: Percent,
      },
      {
        id: 'strong_support_config.min_touches',
        type: 'number',
        label: 'Min Touches',
        defaultValue: 3,
        min: 2,
        max: 10,
        step: 1,
        icon: CheckCircle2,
      },
      {
        id: 'strong_support_config.min_bounce_pct',
        type: 'number',
        label: 'Min Bounce %',
        defaultValue: 2.0,
        min: 0.5,
        max: 10,
        step: 0.5,
        suffix: '%',
        icon: TrendingUp,
      },
      {
        id: 'strong_support_config.min_spacing_days',
        type: 'number',
        label: 'Min Spacing Days',
        defaultValue: 5,
        min: 1,
        max: 30,
        step: 1,
        icon: Clock,
      },
      {
        id: 'strong_support_config.volume_multiplier',
        type: 'number',
        label: 'Volume Multiplier',
        defaultValue: 1.3,
        min: 1.0,
        max: 3.0,
        step: 0.1,
        prefix: '',
        suffix: 'x',
        icon: Volume2,
      },
      {
        id: 'strong_support_config.slope_min',
        type: 'number',
        label: 'Min Slope',
        defaultValue: 0.01,
        min: 0,
        max: 1,
        step: 0.01,
        icon: TrendingUp,
      },
      {
        id: 'strong_support_config.slope_max',
        type: 'number',
        label: 'Max Slope',
        defaultValue: 2.0,
        min: 0.1,
        max: 5,
        step: 0.1,
        icon: TrendingUp,
      },
      {
        id: 'strong_support_config.stop_loss_buffer_pct',
        type: 'number',
        label: 'Stop Loss Buffer %',
        defaultValue: 1.0,
        min: 0.1,
        max: 5,
        step: 0.1,
        suffix: '%',
        icon: TrendingDown,
      },
      {
        id: 'strong_support_config.reclaim_window_bars',
        type: 'number',
        label: 'Reclaim Window (bars)',
        defaultValue: 5,
        min: 3,
        max: 10,
        step: 1,
        icon: Clock,
      },
      {
        id: 'strong_support_config.reclaim_vol_mult',
        type: 'number',
        label: 'Reclaim Volume Mult',
        defaultValue: 1.3,
        min: 1.0,
        max: 2.0,
        step: 0.1,
        prefix: '',
        suffix: 'x',
        icon: Volume2,
      },
      {
        id: 'strong_support_config.reclaim_vol_mult_max',
        type: 'number',
        label: 'Reclaim Vol Mult Max',
        defaultValue: 1.5,
        min: 1.0,
        max: 2.0,
        step: 0.1,
        prefix: '',
        suffix: 'x',
        icon: Volume2,
      },
      {
        id: 'strong_support_config.base_lookahead_bars',
        type: 'number',
        label: 'Base Lookahead (bars)',
        defaultValue: 7,
        min: 3,
        max: 15,
        step: 1,
        icon: Clock,
      },
      {
        id: 'strong_support_config.kickoff_vol_mult',
        type: 'number',
        label: 'Kickoff Volume Mult',
        defaultValue: 1.3,
        min: 1.0,
        max: 2.0,
        step: 0.1,
        prefix: '',
        suffix: 'x',
        icon: Volume2,
      },
    ],
  },
];

// Extended Filters
export const extendedFilters: FilterItem[] = [
  {
    id: 'volume_spike_enabled',
    type: 'toggle-with-detail',
    label: 'Volume Spike Filter',
    shortDescription: 'Today\'s volume ≥ 2× average',
    description: 'Today\'s volume is at least 2× the trailing average. Useful for confirming real moves and avoiding low-liquidity false signals.',
    defaultValue: false,
    enabledKey: 'volume_spike_enabled',
    detailEnabledKey: 'volume_spike_configExpanded',
    configKey: 'volume_spike_config',
    configDefaults: {
      min_multiplier: 2.0,
      lookback_days: 20,
      min_volume_threshold: 100000,
    },
    icon: Volume2,
    strategyKey: 'volume_spike_strategy',
    strategyOptions: ['conservative', 'balanced', 'aggressive', 'custom'],
    strategyPresets: {
      conservative: { min_multiplier: 3.0, lookback_days: 20, min_volume_threshold: 200000 },
      balanced: { min_multiplier: 2.0, lookback_days: 20, min_volume_threshold: 100000 },
      aggressive: { min_multiplier: 1.5, lookback_days: 10, min_volume_threshold: 50000 },
      custom: { min_multiplier: 2.0, lookback_days: 20, min_volume_threshold: 100000 },
    },
    children: [
      {
        id: 'volume_spike_config.min_multiplier',
        type: 'number',
        label: 'Min Multiplier',
        defaultValue: 2.0,
        min: 1.5,
        max: 5.0,
        step: 0.1,
        prefix: '',
        suffix: 'x',
      },
      {
        id: 'volume_spike_config.lookback_days',
        type: 'number',
        label: 'Lookback Days',
        defaultValue: 20,
        min: 5,
        max: 60,
        step: 1,
      },
      {
        id: 'volume_spike_config.min_volume_threshold',
        type: 'number',
        label: 'Min Volume',
        defaultValue: 100000,
        min: 10000,
        max: 1000000,
        step: 10000,
        prefix: '',
        suffix: '',
      },
    ],
  },
  {
    id: 'price_action_enabled',
    type: 'toggle-with-detail',
    label: 'Price Action Filter',
    shortDescription: 'Mostly green candles in lookback window',
    description: 'Stocks where the majority of recent daily candles closed up (green) - a coarse bullish-bias filter that complements MA/RSI rules.',
    defaultValue: false,
    enabledKey: 'price_action_enabled',
    detailEnabledKey: 'price_action_configExpanded',
    configKey: 'price_action_config',
    configDefaults: {
      min_green_candles: 3,
      max_red_candles: 1,
      lookback_days: 5,
    },
    icon: CandlestickChart,
    strategyKey: 'price_action_strategy',
    strategyOptions: ['strict', 'balanced', 'moderate', 'custom'],
    strategyPresets: {
      strict: { min_green_candles: 5, max_red_candles: 0, lookback_days: 5 },
      balanced: { min_green_candles: 3, max_red_candles: 1, lookback_days: 5 },
      moderate: { min_green_candles: 2, max_red_candles: 2, lookback_days: 5 },
      custom: { min_green_candles: 3, max_red_candles: 1, lookback_days: 5 },
    },
    children: [
      {
        id: 'price_action_config.min_green_candles',
        type: 'number',
        label: 'Min Green Candles',
        defaultValue: 3,
        min: 0,
        max: 20,
        step: 1,
      },
      {
        id: 'price_action_config.max_red_candles',
        type: 'number',
        label: 'Max Red Candles',
        defaultValue: 1,
        min: 0,
        max: 20,
        step: 1,
      },
      {
        id: 'price_action_config.lookback_days',
        type: 'number',
        label: 'Lookback Days',
        defaultValue: 5,
        min: 1,
        max: 30,
        step: 1,
      },
    ],
  },
  {
    id: 'breakout_enabled',
    type: 'toggle-with-detail',
    label: 'Breakout Filter',
    shortDescription: 'Close above N-day high with volume',
    description: 'Close above the recent N-day resistance high, optionally requiring volume confirmation. Independent of MA proximity. Pattern → Range Breakout uses consolidation geometry instead.',
    defaultValue: false,
    enabledKey: 'breakout_enabled',
    detailEnabledKey: 'breakout_configExpanded',
    configKey: 'breakout_config',
    configDefaults: {
      resistance_lookback: 50,
      breakout_threshold: 0.02,
      volume_confirmation: true,
    },
    icon: Zap,
    strategyKey: 'breakout_strategy',
    strategyOptions: ['strong', 'balanced', 'early', 'custom'],
    strategyPresets: {
      strong: { resistance_lookback: 50, breakout_threshold: 0.03, volume_confirmation: true },
      balanced: { resistance_lookback: 50, breakout_threshold: 0.02, volume_confirmation: true },
      early: { resistance_lookback: 30, breakout_threshold: 0.01, volume_confirmation: false },
      custom: { resistance_lookback: 50, breakout_threshold: 0.02, volume_confirmation: true },
    },
    children: [
      {
        id: 'breakout_config.resistance_lookback',
        type: 'number',
        label: 'Resistance Lookback',
        defaultValue: 50,
        min: 20,
        max: 200,
        step: 5,
      },
      {
        id: 'breakout_config.breakout_threshold',
        type: 'number',
        label: 'Breakout Threshold %',
        defaultValue: 0.02,
        min: 0.005,
        max: 0.1,
        step: 0.005,
        suffix: '%',
      },
      {
        id: 'breakout_config.volume_confirmation',
        type: 'toggle',
        label: 'Volume Confirmation',
        defaultValue: true,
      },
    ],
  },
  {
    id: 'rsi_recovery_enabled',
    type: 'toggle-with-detail',
    label: 'RSI Recovery Filter',
    shortDescription: 'Buy the dip - oversold bounce',
    description: 'Finds oversold stocks bouncing back (RSI 30-50). Classic "buy the dip" strategy. Works independently of MA proximity filter.',
    defaultValue: false,
    enabledKey: 'rsi_recovery_enabled',
    detailEnabledKey: 'rsi_recovery_configExpanded',
    configKey: 'rsi_recovery_config',
    configDefaults: {
      oversold_threshold: 30,
      recovery_min: 30,
      recovery_max: 50,
      lookback_days: 10,
      rsi_period: 14,
      require_volume_confirmation: true,
    },
    icon: Activity,
    strategyKey: 'rsi_recovery_strategy',
    strategyOptions: ['conservative', 'balanced', 'aggressive', 'custom'],
    strategyPresets: {
      conservative: { oversold_threshold: 25, recovery_min: 35, recovery_max: 45, lookback_days: 14, rsi_period: 14, require_volume_confirmation: true },
      balanced: { oversold_threshold: 30, recovery_min: 30, recovery_max: 50, lookback_days: 10, rsi_period: 14, require_volume_confirmation: true },
      aggressive: { oversold_threshold: 35, recovery_min: 25, recovery_max: 55, lookback_days: 5, rsi_period: 7, require_volume_confirmation: false },
      custom: { oversold_threshold: 30, recovery_min: 30, recovery_max: 50, lookback_days: 10, rsi_period: 14, require_volume_confirmation: true },
    },
    children: [
      {
        id: 'rsi_recovery_config.oversold_threshold',
        type: 'number',
        label: 'Oversold Threshold',
        defaultValue: 30,
        min: 20,
        max: 40,
        step: 1,
      },
      {
        id: 'rsi_recovery_config.recovery_min',
        type: 'number',
        label: 'Recovery RSI Min',
        defaultValue: 30,
        min: 25,
        max: 60,
        step: 1,
      },
      {
        id: 'rsi_recovery_config.recovery_max',
        type: 'number',
        label: 'Recovery RSI Max',
        defaultValue: 50,
        min: 35,
        max: 70,
        step: 1,
      },
      {
        id: 'rsi_recovery_config.lookback_days',
        type: 'number',
        label: 'Lookback Days',
        defaultValue: 10,
        min: 3,
        max: 30,
        step: 1,
      },
      {
        id: 'rsi_recovery_config.rsi_period',
        type: 'number',
        label: 'RSI Period',
        defaultValue: 14,
        min: 7,
        max: 28,
        step: 1,
      },
      {
        id: 'rsi_recovery_config.require_volume_confirmation',
        type: 'toggle',
        label: 'Volume Confirmation',
        defaultValue: true,
      },
    ],
  },
  {
    id: 'stochastic_enabled',
    type: 'toggle-with-detail',
    label: 'Stochastic Recovery Filter',
    shortDescription: 'Buy the dip - Stochastic bounce',
    description: 'Finds oversold stocks bouncing back using Stochastic Oscillator (%K crossing above %D). Similar to RSI Recovery but uses Stochastic for confirmation. Works independently of MA proximity filter.',
    defaultValue: false,
    enabledKey: 'stochastic_enabled',
    detailEnabledKey: 'stochastic_configExpanded',
    configKey: 'stochastic_config',
    configDefaults: {
      oversold_threshold: 20,
      recovery_min: 20,
      recovery_max: 50,
      lookback_days: 10,
      k_period: 14,
      d_period: 3,
      require_crossover: true,
      require_volume_confirmation: true,
    },
    icon: Activity,
    strategyKey: 'stochastic_strategy',
    strategyOptions: ['conservative', 'balanced', 'aggressive', 'custom'],
    strategyPresets: {
      conservative: { oversold_threshold: 15, recovery_min: 20, recovery_max: 40, lookback_days: 5, k_period: 14, d_period: 3, require_crossover: true, require_volume_confirmation: true },
      balanced: { oversold_threshold: 20, recovery_min: 20, recovery_max: 50, lookback_days: 10, k_period: 14, d_period: 3, require_crossover: true, require_volume_confirmation: true },
      aggressive: { oversold_threshold: 25, recovery_min: 15, recovery_max: 60, lookback_days: 15, k_period: 14, d_period: 3, require_crossover: false, require_volume_confirmation: false },
      custom: { oversold_threshold: 20, recovery_min: 20, recovery_max: 50, lookback_days: 10, k_period: 14, d_period: 3, require_crossover: true, require_volume_confirmation: true },
    },
    children: [
      {
        id: 'stochastic_config.oversold_threshold',
        type: 'number',
        label: 'Oversold Threshold',
        defaultValue: 20,
        min: 10,
        max: 30,
        step: 1,
      },
      {
        id: 'stochastic_config.recovery_min',
        type: 'number',
        label: 'Recovery %K Min',
        defaultValue: 20,
        min: 15,
        max: 40,
        step: 1,
      },
      {
        id: 'stochastic_config.recovery_max',
        type: 'number',
        label: 'Recovery %K Max',
        defaultValue: 50,
        min: 30,
        max: 70,
        step: 1,
      },
      {
        id: 'stochastic_config.lookback_days',
        type: 'number',
        label: 'Lookback Days',
        defaultValue: 10,
        min: 3,
        max: 30,
        step: 1,
      },
      {
        id: 'stochastic_config.k_period',
        type: 'number',
        label: '%K Period',
        defaultValue: 14,
        min: 9,
        max: 21,
        step: 1,
      },
      {
        id: 'stochastic_config.d_period',
        type: 'number',
        label: '%D Period',
        defaultValue: 3,
        min: 3,
        max: 7,
        step: 1,
      },
      {
        id: 'stochastic_config.require_crossover',
        type: 'toggle',
        label: 'Require %K > %D',
        defaultValue: true,
      },
      {
        id: 'stochastic_config.require_volume_confirmation',
        type: 'toggle',
        label: 'Volume Confirmation',
        defaultValue: true,
      },
    ],
  },
  {
    id: 'ma_bounce_reversal_enabled',
    type: 'toggle-with-detail',
    label: 'MA Bounce Reversal Filter',
    shortDescription: 'Bounce off MA after a decline',
    description: 'Stocks that touched a moving average (default MA150) following a multi-day pullback and printed a reversal candle - typical buy-the-dip setup.',
    defaultValue: false,
    enabledKey: 'ma_bounce_reversal_enabled',
    detailEnabledKey: 'ma_bounce_reversal_configExpanded',
    configKey: 'ma_bounce_reversal_config',
    configDefaults: {
      min_loss_pct: 4.0,
      min_reversal_pct: 0.5,
      touch_tolerance_pct: 3.0,
      max_reversal_days: 3,
      ma_selection: ['MA_50', 'MA_100', 'MA_150', 'MA_200'],
      require_close_above_ma: false,
      require_volume_confirmation: false,
      require_price_above_ma_before_drop: true,
      max_reversal_to_loss_ratio: 1.0,
    },
    icon: RefreshCcw,
    strategyKey: 'ma_bounce_reversal_strategy',
    strategyOptions: ['conservative', 'balanced', 'aggressive', 'custom'],
    strategyPresets: {
      conservative: { min_loss_pct: 6.0, min_reversal_pct: 1.0, touch_tolerance_pct: 2.0, max_reversal_days: 2, ma_selection: ['MA_100', 'MA_150', 'MA_200'], require_close_above_ma: true, require_volume_confirmation: true, require_price_above_ma_before_drop: true, max_reversal_to_loss_ratio: 0.5 },
      balanced: { min_loss_pct: 4.0, min_reversal_pct: 0.5, touch_tolerance_pct: 3.0, max_reversal_days: 3, ma_selection: ['MA_50', 'MA_100', 'MA_150', 'MA_200'], require_close_above_ma: false, require_volume_confirmation: false, require_price_above_ma_before_drop: true, max_reversal_to_loss_ratio: 1.0 },
      aggressive: { min_loss_pct: 2.0, min_reversal_pct: 0.3, touch_tolerance_pct: 5.0, max_reversal_days: 5, ma_selection: ['MA_50', 'MA_100', 'MA_150', 'MA_200'], require_close_above_ma: false, require_volume_confirmation: false, require_price_above_ma_before_drop: false, max_reversal_to_loss_ratio: 2.0 },
      custom: { min_loss_pct: 4.0, min_reversal_pct: 0.5, touch_tolerance_pct: 3.0, max_reversal_days: 3, ma_selection: ['MA_50', 'MA_100', 'MA_150', 'MA_200'], require_close_above_ma: false, require_volume_confirmation: false, require_price_above_ma_before_drop: true, max_reversal_to_loss_ratio: 1.0 },
    },
    children: [
      {
        id: 'ma_bounce_reversal_config.min_loss_pct',
        type: 'number',
        label: 'Min Loss %',
        defaultValue: 4.0,
        min: 1,
        max: 15,
        step: 0.5,
        suffix: '%',
      },
      {
        id: 'ma_bounce_reversal_config.min_reversal_pct',
        type: 'number',
        label: 'Min Reversal %',
        defaultValue: 0.5,
        min: 0.1,
        max: 5,
        step: 0.1,
        suffix: '%',
      },
      {
        id: 'ma_bounce_reversal_config.touch_tolerance_pct',
        type: 'number',
        label: 'Touch Tolerance %',
        defaultValue: 3.0,
        min: 0.5,
        max: 10,
        step: 0.5,
        suffix: '%',
      },
      {
        id: 'ma_bounce_reversal_config.max_reversal_days',
        type: 'number',
        label: 'Max Reversal Days',
        defaultValue: 3,
        min: 1,
        max: 10,
        step: 1,
      },
      {
        id: 'ma_bounce_reversal_config.ma_selection',
        type: 'card-checkbox-group',
        label: 'MA Selection',
        defaultValue: ['MA_50', 'MA_100', 'MA_150', 'MA_200'],
        hasSelectAll: true,
        options: [
          { value: 'MA_50', label: 'MA 50' },
          { value: 'MA_100', label: 'MA 100' },
          { value: 'MA_150', label: 'MA 150' },
          { value: 'MA_200', label: 'MA 200' },
        ],
      },
      {
        id: 'ma_bounce_reversal_config.require_close_above_ma',
        type: 'toggle',
        label: 'Close Above MA',
        defaultValue: false,
      },
      {
        id: 'ma_bounce_reversal_config.require_volume_confirmation',
        type: 'toggle',
        label: 'Volume Confirmation',
        defaultValue: false,
      },
      {
        id: 'ma_bounce_reversal_config.require_price_above_ma_before_drop',
        type: 'toggle',
        label: 'Price Above MA Before Drop',
        defaultValue: true,
      },
      {
        id: 'ma_bounce_reversal_config.max_reversal_to_loss_ratio',
        type: 'number',
        label: 'Max Reversal/Loss Ratio',
        defaultValue: 1.0,
        min: 0.2,
        max: 3.0,
        step: 0.1,
      },
    ],
  },
  {
    id: 'ma150_v_reversal_enabled',
    type: 'toggle-with-detail',
    label: 'MA150 V-Reversal Filter',
    shortDescription: 'V-shaped reversal in MA150',
    description: 'Quick summary: Detects early inflection zone when MA150 turns upward from a bearish V-shape pattern. What it does: Identifies stocks where MA150 (150-day moving average) was trending down, forms a V-bottom, then the slope crosses from negative to positive (trigger point), with price breaking above MA150 and RSI confirmation. When to use: Catch early reversal signals at the inflection point - more responsive than waiting for price to break out. Best for swing traders looking for early entries.',
    defaultValue: false,
    enabledKey: 'ma150_v_reversal_enabled',
    detailEnabledKey: 'ma150_v_reversal_configExpanded',
    configKey: 'ma150_v_reversal_config',
    configDefaults: {
      min_bearish_bars: 150,
      min_v_depth_pct: 5.0,
      max_v_depth_pct: 25.0,
      slope_lookback: 10,
      require_price_above_ma: true,
      require_rsi_confirmation: true,
      rsi_confirmation_threshold: 40.0,
      max_recovery_pct: 40.0,
      max_days_since_v_bottom: 30,
      max_price_vs_ma_pct: 10.0,
      rsi_mode: 'level',
      min_rsi_delta: 10.0,
      rsi_oversold_cap: 35.0,
      slope_majority_threshold: 100,
      deep_drawdown_mode: false,
      max_v_depth_deep: 90.0,
      require_ma200_support: false,
      max_ma200_spread_pct: 8.0,
      support_epsilon_pct: 5.0,
    },
    icon: TrendingUp,
    strategyKey: 'ma150_v_reversal_strategy',
    strategyOptions: ['conservative', 'balanced', 'aggressive', 'deep_drawdown', 'custom'],
    strategyPresets: {
      conservative: { min_bearish_bars: 150, min_v_depth_pct: 8.0, max_v_depth_pct: 20.0, slope_lookback: 10, require_price_above_ma: true, require_rsi_confirmation: true, rsi_confirmation_threshold: 45.0, max_recovery_pct: 25.0, max_days_since_v_bottom: 20, max_price_vs_ma_pct: 8.0, rsi_mode: 'level', min_rsi_delta: 10.0, rsi_oversold_cap: 35.0, slope_majority_threshold: 100, deep_drawdown_mode: false, max_v_depth_deep: 90.0, require_ma200_support: false, max_ma200_spread_pct: 8.0, support_epsilon_pct: 5.0 },
      balanced: { min_bearish_bars: 150, min_v_depth_pct: 5.0, max_v_depth_pct: 25.0, slope_lookback: 10, require_price_above_ma: true, require_rsi_confirmation: true, rsi_confirmation_threshold: 40.0, max_recovery_pct: 40.0, max_days_since_v_bottom: 30, max_price_vs_ma_pct: 10.0, rsi_mode: 'level', min_rsi_delta: 10.0, rsi_oversold_cap: 35.0, slope_majority_threshold: 100, deep_drawdown_mode: false, max_v_depth_deep: 90.0, require_ma200_support: false, max_ma200_spread_pct: 8.0, support_epsilon_pct: 5.0 },
      aggressive: { min_bearish_bars: 120, min_v_depth_pct: 3.0, max_v_depth_pct: 30.0, slope_lookback: 5, require_price_above_ma: false, require_rsi_confirmation: false, rsi_confirmation_threshold: 30.0, max_recovery_pct: 50.0, max_days_since_v_bottom: 45, max_price_vs_ma_pct: 15.0, rsi_mode: 'off', min_rsi_delta: 10.0, rsi_oversold_cap: 35.0, slope_majority_threshold: 50, deep_drawdown_mode: false, max_v_depth_deep: 90.0, require_ma200_support: false, max_ma200_spread_pct: 10.0, support_epsilon_pct: 5.0 },
      deep_drawdown: { min_bearish_bars: 180, min_v_depth_pct: 30.0, max_v_depth_pct: 25.0, slope_lookback: 10, require_price_above_ma: true, require_rsi_confirmation: true, rsi_mode: 'recovery_delta', rsi_confirmation_threshold: 35.0, min_rsi_delta: 8.0, rsi_oversold_cap: 35.0, max_recovery_pct: 50.0, max_days_since_v_bottom: 45, max_price_vs_ma_pct: 15.0, deep_drawdown_mode: true, require_ma200_support: false, max_ma200_spread_pct: 10.0, slope_majority_threshold: 70, support_epsilon_pct: 5.0 },
      custom: { min_bearish_bars: 150, min_v_depth_pct: 5.0, max_v_depth_pct: 25.0, slope_lookback: 10, require_price_above_ma: true, require_rsi_confirmation: true, rsi_confirmation_threshold: 40.0, max_recovery_pct: 40.0, max_days_since_v_bottom: 30, max_price_vs_ma_pct: 10.0, rsi_mode: 'level', min_rsi_delta: 10.0, rsi_oversold_cap: 35.0, slope_majority_threshold: 100, deep_drawdown_mode: false, max_v_depth_deep: 90.0, require_ma200_support: false, max_ma200_spread_pct: 8.0, support_epsilon_pct: 5.0 },
    },
    children: [
      {
        id: 'ma150_v_reversal_config.min_bearish_bars',
        type: 'number',
        label: 'Min Bearish Bars',
        defaultValue: 150,
        min: 100,
        max: 200,
        step: 10,
      },
      {
        id: 'ma150_v_reversal_config.min_v_depth_pct',
        type: 'number',
        label: 'Min V-Depth %',
        defaultValue: 5.0,
        min: 1.0,
        max: 30.0,
        step: 0.5,
        suffix: '%',
      },
      {
        id: 'ma150_v_reversal_config.max_v_depth_pct',
        type: 'number',
        label: 'Max V-Depth %',
        defaultValue: 25.0,
        min: 10.0,
        max: 50.0,
        step: 1.0,
        suffix: '%',
      },
      {
        id: 'ma150_v_reversal_config.slope_lookback',
        type: 'number',
        label: 'Slope Lookback',
        defaultValue: 10,
        min: 3,
        max: 20,
        step: 1,
      },
      {
        id: 'ma150_v_reversal_config.require_price_above_ma',
        type: 'toggle',
        label: 'Price Above MA150',
        defaultValue: true,
      },
      {
        id: 'ma150_v_reversal_config.require_rsi_confirmation',
        type: 'toggle',
        label: 'RSI Confirmation',
        defaultValue: true,
      },
      {
        id: 'ma150_v_reversal_config.rsi_confirmation_threshold',
        type: 'number',
        label: 'RSI Threshold',
        defaultValue: 40.0,
        min: 20.0,
        max: 60.0,
        step: 5.0,
      },
      {
        id: 'ma150_v_reversal_config.max_recovery_pct',
        type: 'number',
        label: 'Max Recovery %',
        defaultValue: 40.0,
        min: 10.0,
        max: 80.0,
        step: 5.0,
        suffix: '%',
      },
      {
        id: 'ma150_v_reversal_config.max_days_since_v_bottom',
        type: 'number',
        label: 'Max Days Since V-Bottom',
        defaultValue: 30,
        min: 5,
        max: 60,
        step: 5,
      },
      {
        id: 'ma150_v_reversal_config.max_price_vs_ma_pct',
        type: 'number',
        label: 'Max Price vs MA150 %',
        defaultValue: 10.0,
        min: 1.0,
        max: 30.0,
        step: 1.0,
        suffix: '%',
      },
      {
        id: 'ma150_v_reversal_config.deep_drawdown_mode',
        type: 'toggle',
        label: 'Deep Drawdown Mode',
        defaultValue: false,
      },
      {
        id: 'ma150_v_reversal_config.rsi_mode',
        type: 'select',
        label: 'RSI Mode',
        defaultValue: 'level',
        options: [
          { value: 'level', label: 'Level (above threshold)' },
          { value: 'recovery_delta', label: 'Recovery Delta (rising from lows)' },
          { value: 'off', label: 'Off' }
        ],
      },
      {
        id: 'ma150_v_reversal_config.min_rsi_delta',
        type: 'number',
        label: 'Min RSI Delta',
        defaultValue: 10.0,
        min: 5.0,
        max: 25.0,
        step: 1.0,
      },
      {
        id: 'ma150_v_reversal_config.slope_majority_threshold',
        type: 'number',
        label: 'Slope Majority %',
        defaultValue: 100,
        min: 50,
        max: 100,
        step: 10,
      },
      {
        id: 'ma150_v_reversal_config.require_ma200_support',
        type: 'toggle',
        label: 'MA200 Convergence',
        defaultValue: false,
      },
    ],
  },
  {
    id: 'support_retest_enabled',
    type: 'toggle-with-detail',
    label: 'Support Retest Filter',
    shortDescription: 'Multi-touch support zones',
    description: 'Finds stocks retesting validated support zones (min-price based). Works independently of MA proximity filter.',
    defaultValue: false,
    enabledKey: 'support_retest_enabled',
    detailEnabledKey: 'support_retest_configExpanded',
    configKey: 'support_retest_config',
    configDefaults: {
      support_window_days: 90,
      support_tolerance_pct: 0.02,
      support_min_touches: 2,
      min_spacing_days: 3,
      break_tolerance_pct: 0.01,
      require_rejection_wick: false,
      rejection_wick_ratio_min: 0.3,
      require_volume_confirmation: false,
      volume_multiplier: 1.2,
      volume_lookback_days: 20,
    },
    icon: AlertTriangle,
    strategyKey: 'support_retest_strategy',
    strategyOptions: ['conservative', 'balanced', 'aggressive', 'custom'],
    strategyPresets: {
      conservative: { support_window_days: 120, support_tolerance_pct: 0.01, support_min_touches: 3, min_spacing_days: 5, break_tolerance_pct: 0.005, require_rejection_wick: true, rejection_wick_ratio_min: 0.4, require_volume_confirmation: true, volume_multiplier: 1.5, volume_lookback_days: 30 },
      balanced: { support_window_days: 90, support_tolerance_pct: 0.02, support_min_touches: 2, min_spacing_days: 3, break_tolerance_pct: 0.01, require_rejection_wick: false, rejection_wick_ratio_min: 0.3, require_volume_confirmation: false, volume_multiplier: 1.2, volume_lookback_days: 20 },
      aggressive: { support_window_days: 60, support_tolerance_pct: 0.03, support_min_touches: 2, min_spacing_days: 1, break_tolerance_pct: 0.02, require_rejection_wick: false, rejection_wick_ratio_min: 0.2, require_volume_confirmation: false, volume_multiplier: 1.0, volume_lookback_days: 10 },
      custom: { support_window_days: 90, support_tolerance_pct: 0.02, support_min_touches: 2, min_spacing_days: 3, break_tolerance_pct: 0.01, require_rejection_wick: false, rejection_wick_ratio_min: 0.3, require_volume_confirmation: false, volume_multiplier: 1.2, volume_lookback_days: 20 },
    },
    children: [
      {
        id: 'support_retest_config.support_window_days',
        type: 'number',
        label: 'Support Window Days',
        defaultValue: 90,
        min: 30,
        max: 180,
        step: 10,
      },
      {
        id: 'support_retest_config.support_tolerance_pct',
        type: 'number',
        label: 'Support Tolerance %',
        defaultValue: 0.02,
        min: 0.005,
        max: 0.05,
        step: 0.005,
        suffix: '%',
      },
      {
        id: 'support_retest_config.support_min_touches',
        type: 'number',
        label: 'Min Touches',
        defaultValue: 2,
        min: 2,
        max: 5,
        step: 1,
      },
      {
        id: 'support_retest_config.min_spacing_days',
        type: 'number',
        label: 'Min Spacing Days',
        defaultValue: 3,
        min: 1,
        max: 15,
        step: 1,
      },
      {
        id: 'support_retest_config.break_tolerance_pct',
        type: 'number',
        label: 'Break Tolerance %',
        defaultValue: 0.01,
        min: 0.005,
        max: 0.03,
        step: 0.005,
        suffix: '%',
      },
      {
        id: 'support_retest_config.require_rejection_wick',
        type: 'toggle',
        label: 'Require Rejection Wick',
        defaultValue: false,
      },
      {
        id: 'support_retest_config.rejection_wick_ratio_min',
        type: 'number',
        label: 'Min Wick Ratio',
        defaultValue: 0.3,
        min: 0.1,
        max: 0.6,
        step: 0.05,
      },
      {
        id: 'support_retest_config.require_volume_confirmation',
        type: 'toggle',
        label: 'Volume Confirmation',
        defaultValue: false,
      },
      {
        id: 'support_retest_config.volume_multiplier',
        type: 'number',
        label: 'Volume Multiplier',
        defaultValue: 1.2,
        min: 1.0,
        max: 2.5,
        step: 0.1,
        prefix: '',
        suffix: 'x',
      },
      {
        id: 'support_retest_config.volume_lookback_days',
        type: 'number',
        label: 'Volume Lookback Days',
        defaultValue: 20,
        min: 5,
        max: 60,
        step: 5,
      },
    ],
  },
];

// Sidebar categories (includes Finviz)
export const filterCategories: FilterCategoryItem[] = [
  {
    id: 'market',
    label: 'Market Criteria',
    icon: BarChart2,
    description: 'Market cap, price targets, index filters',
    filterIds: marketCriteriaFilters.map((f) => f.id),
  },
  {
    id: 'ma',
    label: 'Moving Averages',
    icon: LineChart,
    description: 'MA selection, proximity, trend period, timeframe (Daily/Weekly)',
    filterIds: maCriteriaFilters.map((f) => f.id),
  },
  {
    id: 'volume',
    label: 'Volume & Momentum',
    icon: Volume2,
    description: 'Volume surge threshold, high volume filter',
    filterIds: volumeMomentumFilters.map((f) => f.id),
  },
  {
    id: 'patterns',
    label: 'Patterns',
    icon: CandlestickChart,
    description: 'Select specific chart patterns to identify trading opportunities. Leave all unchecked to include all patterns.',
    filterIds: patternFilters.map((f) => f.id),
  },
  {
    id: 'advanced',
    label: 'Advanced',
    icon: Target,
    description: 'Golden cross, Fibonacci, support',
    filterIds: advancedFilters.map((f) => f.id),
  },
  {
    id: 'extended',
    label: 'Extended',
    icon: Zap,
    description: 'Advanced technical filters that work independently of MA proximity when "No MA Filter" is selected.',
    filterIds: extendedFilters.map((f) => f.id),
  },
];

// All filters combined
export const allFilters: FilterItem[] = [
  ...marketCriteriaFilters,
  ...maCriteriaFilters,
  ...volumeMomentumFilters,
  ...patternFilters,
  ...advancedFilters,
  ...extendedFilters,
];

// Create default values map
export const defaultFilterValues: DefaultValuesMap = createDefaultValuesMap(allFilters);

/**
 * Maps pill / control ids (filter id, enabledKey, child ids, etc.) to sidebar category id.
 * Used to show only relevant "Active filters" chips for the category being edited.
 */
export const filterPillIdToCategoryId: Map<string, string> = (() => {
  const m = new Map<string, string>();
  const reg = (categoryId: string, key: string | undefined) => {
    if (key) m.set(key, categoryId);
  };
  filterCategories.forEach((cat) => {
    cat.filterIds.forEach((fid) => {
      const filter = allFilters.find((f) => f.id === fid);
      if (!filter) return;
      reg(cat.id, filter.id);
      reg(cat.id, filter.enabledKey);
      reg(cat.id, filter.detailEnabledKey);
      reg(cat.id, filter.strategyKey);
      filter.children?.forEach((child) => reg(cat.id, child.id));
    });
  });
  return m;
})();

// Lazy registration flag for finviz filters to avoid TDZ issues with circular dependencies
let finvizFiltersRegistered = false;

// Register Finviz filters under 'finviz' category (lazy initialization)
const ensureFinvizFiltersRegistered = () => {
  if (finvizFiltersRegistered) return;
  finvizFilters.forEach((filter) => {
    filterPillIdToCategoryId.set(filter.id, 'finviz');
    filter.children?.forEach((child) => filterPillIdToCategoryId.set(child.id, 'finviz'));
  });
  finvizFiltersRegistered = true;
};

// Helper to get category filters (excludes uiHidden filters)
export const getCategoryFilters = (categoryId: string): FilterItem[] => {
  // Lazy registration for finviz filters to avoid TDZ issues with circular dependencies
  ensureFinvizFiltersRegistered();

  const category = filterCategories.find((c) => c.id === categoryId);
  if (!category) return [];
  return allFilters.filter((f) => category.filterIds.includes(f.id) && !f.uiHidden);
};

// Helper to get filter by ID
export const getFilterById = (id: string): FilterItem | undefined => {
  // Lazy registration for finviz filters to avoid TDZ issues with circular dependencies
  ensureFinvizFiltersRegistered();

  return allFilters.find((f) => f.id === id);
};

// ============================================================================
// Finviz-style Filters
// ============================================================================

// Finviz Descriptive Filters
export const finvizDescriptiveFilters: FilterItem[] = [
  {
    id: 'finviz_exchange',
    type: 'card-checkbox-group',
    label: 'Exchange',
    shortDescription: 'Filter by stock exchange',
    description: 'Filter stocks by their listing exchange (NASDAQ, NYSE, AMEX).',
    defaultValue: [],
    hasSelectAll: true,
    options: [
      { value: 'NASDAQ', label: 'NASDAQ', subtitle: 'National Association of Securities Dealers' },
      { value: 'NYSE', label: 'NYSE', subtitle: 'New York Stock Exchange' },
      { value: 'AMEX', label: 'AMEX', subtitle: 'American Stock Exchange' },
    ],
    icon: BarChart2,
  },
  {
    id: 'finviz_index',
    type: 'card-checkbox-group',
    label: 'Index',
    shortDescription: 'Filter by market index',
    description: 'Filter stocks belonging to specific market indices.',
    defaultValue: [],
    hasSelectAll: true,
    options: [
      { value: 'SP500', label: 'S&P 500', subtitle: '500 large-cap US companies' },
      { value: 'NASDAQ100', label: 'NASDAQ 100', subtitle: 'Top 100 non-financial NASDAQ stocks' },
      { value: 'DOW30', label: 'Dow 30', subtitle: '30 blue-chip stocks' },
    ],
    icon: Grid3X3,
  },
  {
    id: 'finviz_sector',
    type: 'card-checkbox-group',
    label: 'Sector',
    shortDescription: 'Filter by sector',
    description: 'Filter stocks by industry sector.',
    defaultValue: [],
    hasSelectAll: true,
    options: [
      { value: 'Technology', label: 'Technology', subtitle: 'Software, Hardware, Semiconductors' },
      { value: 'Healthcare', label: 'Healthcare', subtitle: 'Biotech, Pharma, Medical' },
      { value: 'Financial', label: 'Financial', subtitle: 'Banks, Insurance, REITs' },
      { value: 'Consumer', label: 'Consumer', subtitle: 'Retail, Media, Restaurants' },
      { value: 'Industrial', label: 'Industrial', subtitle: 'Aerospace, Defense, Construction' },
      { value: 'Energy', label: 'Energy', subtitle: 'Oil, Gas, Renewable' },
      { value: 'Utilities', label: 'Utilities', subtitle: 'Electric, Water, Gas' },
      { value: 'Materials', label: 'Materials', subtitle: 'Chemicals, Mining, Paper' },
      { value: 'Real Estate', label: 'Real Estate', subtitle: 'REITs, Development' },
      { value: 'Communications', label: 'Communications', subtitle: 'Telecom, Media, Internet' },
    ],
    icon: Layers,
  },
  {
    id: 'finviz_industry',
    type: 'card-checkbox-group',
    label: 'Industry',
    shortDescription: 'Filter by specific industry',
    description: 'Filter stocks by specific industry within a sector.',
    defaultValue: [],
    hasSelectAll: true,
    options: [
      { value: 'Software', label: 'Software', subtitle: 'Application, Infrastructure' },
      { value: 'Semiconductors', label: 'Semiconductors', subtitle: 'Chips, Equipment' },
      { value: 'Hardware', label: 'Hardware', subtitle: 'Electronics, Storage' },
      { value: 'Biotechnology', label: 'Biotechnology', subtitle: 'Life Sciences' },
      { value: 'Pharmaceuticals', label: 'Pharmaceuticals', subtitle: 'Drug development' },
      { value: 'Banks', label: 'Banks', subtitle: 'Commercial, Investment' },
      { value: 'Insurance', label: 'Insurance', subtitle: 'Life, Property, Health' },
      { value: 'Retail', label: 'Retail', subtitle: 'Stores, E-commerce' },
      { value: 'Restaurants', label: 'Restaurants', subtitle: 'Food service' },
      { value: 'Aerospace', label: 'Aerospace', subtitle: 'Defense, Space' },
    ],
    icon: Activity,
  },
  {
    id: 'finviz_country',
    type: 'card-checkbox-group',
    label: 'Country',
    shortDescription: 'Filter by country',
    description: 'Filter stocks by country of headquarters.',
    defaultValue: [],
    hasSelectAll: true,
    options: [
      { value: 'USA', label: 'USA', subtitle: 'United States' },
      { value: 'China', label: 'China', subtitle: 'People\'s Republic of China' },
      { value: 'Germany', label: 'Germany', subtitle: 'Federal Republic of Germany' },
      { value: 'UK', label: 'UK', subtitle: 'United Kingdom' },
      { value: 'Japan', label: 'Japan', subtitle: 'Japan' },
      { value: 'France', label: 'France', subtitle: 'French Republic' },
      { value: 'Canada', label: 'Canada', subtitle: 'Canada' },
      { value: 'Australia', label: 'Australia', subtitle: 'Commonwealth of Australia' },
      { value: 'India', label: 'India', subtitle: 'Republic of India' },
      { value: 'Taiwan', label: 'Taiwan', subtitle: 'Republic of China (Taiwan)' },
    ],
    icon: Landmark,
  },
  {
    id: 'finviz_market_cap',
    type: 'select',
    label: 'Market Cap',
    shortDescription: 'Filter by market capitalization',
    description: 'Filter stocks by market capitalization size.',
    defaultValue: '',
    options: [
      { value: '', label: 'Any', subtitle: 'All market caps' },
      { value: 'Mega', label: 'Mega ($200B+)', subtitle: 'Largest companies' },
      { value: 'Large', label: 'Large ($10B-$200B)', subtitle: 'Large-cap stocks' },
      { value: 'Mid', label: 'Mid ($2B-$10B)', subtitle: 'Mid-cap stocks' },
      { value: 'Small', label: 'Small ($300M-$2B)', subtitle: 'Small-cap stocks' },
      { value: 'Micro', label: 'Micro (<$300M)', subtitle: 'Micro-cap stocks' },
    ],
    icon: DollarSign,
  },
];

// Finviz Fundamental Filters
export const finvizFundamentalFilters: FilterItem[] = [
  {
    id: 'finviz_pe_min',
    type: 'number',
    label: 'P/E Ratio Min',
    shortDescription: 'Minimum P/E ratio',
    description: 'Minimum price-to-earnings ratio.',
    defaultValue: undefined,
    min: 0,
    max: 500,
    step: 1,
    prefix: '',
    suffix: '',
    icon: TrendingUp,
  },
  {
    id: 'finviz_pe_max',
    type: 'number',
    label: 'P/E Ratio Max',
    shortDescription: 'Maximum P/E ratio',
    description: 'Maximum price-to-earnings ratio.',
    defaultValue: undefined,
    min: 0,
    max: 500,
    step: 1,
    prefix: '',
    suffix: '',
    icon: TrendingUp,
  },
  {
    id: 'finviz_forward_pe_min',
    type: 'number',
    label: 'Forward P/E Min',
    shortDescription: 'Minimum forward P/E',
    description: 'Minimum forward price-to-earnings ratio.',
    defaultValue: undefined,
    min: 0,
    max: 500,
    step: 1,
    prefix: '',
    suffix: '',
    icon: TrendingUp,
  },
  {
    id: 'finviz_forward_pe_max',
    type: 'number',
    label: 'Forward P/E Max',
    shortDescription: 'Maximum forward P/E',
    description: 'Maximum forward price-to-earnings ratio.',
    defaultValue: undefined,
    min: 0,
    max: 500,
    step: 1,
    prefix: '',
    suffix: '',
    icon: TrendingUp,
  },
  {
    id: 'finviz_peg_min',
    type: 'number',
    label: 'PEG Ratio Min',
    shortDescription: 'Minimum PEG ratio',
    description: 'Minimum price/earnings to growth ratio.',
    defaultValue: undefined,
    min: 0,
    max: 10,
    step: 0.1,
    prefix: '',
    suffix: '',
    icon: TrendingUp,
  },
  {
    id: 'finviz_peg_max',
    type: 'number',
    label: 'PEG Ratio Max',
    shortDescription: 'Maximum PEG ratio',
    description: 'Maximum price/earnings to growth ratio.',
    defaultValue: undefined,
    min: 0,
    max: 10,
    step: 0.1,
    prefix: '',
    suffix: '',
    icon: TrendingUp,
  },
  {
    id: 'finviz_ps_min',
    type: 'number',
    label: 'P/Sales Min',
    shortDescription: 'Minimum price to sales',
    description: 'Minimum price-to-sales ratio.',
    defaultValue: undefined,
    min: 0,
    max: 100,
    step: 0.1,
    prefix: '',
    suffix: '',
    icon: TrendingUp,
  },
  {
    id: 'finviz_ps_max',
    type: 'number',
    label: 'P/Sales Max',
    shortDescription: 'Maximum price to sales',
    description: 'Maximum price-to-sales ratio.',
    defaultValue: undefined,
    min: 0,
    max: 100,
    step: 0.1,
    prefix: '',
    suffix: '',
    icon: TrendingUp,
  },
  {
    id: 'finviz_pb_min',
    type: 'number',
    label: 'P/B Ratio Min',
    shortDescription: 'Minimum price to book',
    description: 'Minimum price-to-book ratio.',
    defaultValue: undefined,
    min: 0,
    max: 50,
    step: 0.1,
    prefix: '',
    suffix: '',
    icon: TrendingUp,
  },
  {
    id: 'finviz_pb_max',
    type: 'number',
    label: 'P/B Ratio Max',
    shortDescription: 'Maximum price to book',
    description: 'Maximum price-to-book ratio.',
    defaultValue: undefined,
    min: 0,
    max: 50,
    step: 0.1,
    prefix: '',
    suffix: '',
    icon: TrendingUp,
  },
  {
    id: 'finviz_dividend_min',
    type: 'number',
    label: 'Dividend Yield Min',
    shortDescription: 'Minimum dividend yield %',
    description: 'Minimum annual dividend yield percentage.',
    defaultValue: undefined,
    min: 0,
    max: 20,
    step: 0.1,
    prefix: '',
    suffix: '%',
    icon: Percent,
  },
  {
    id: 'finviz_dividend_max',
    type: 'number',
    label: 'Dividend Yield Max',
    shortDescription: 'Maximum dividend yield %',
    description: 'Maximum annual dividend yield percentage.',
    defaultValue: undefined,
    min: 0,
    max: 20,
    step: 0.1,
    prefix: '',
    suffix: '%',
    icon: Percent,
  },
  {
    id: 'finviz_eps_growth',
    type: 'select',
    label: 'EPS Growth (TTM)',
    shortDescription: 'Earnings per share growth',
    description: 'Filter by earnings per share growth rate (TTM).',
    defaultValue: '',
    options: [
      { value: '', label: 'Any', subtitle: 'No filter' },
      { value: 'negative', label: 'Negative (<0%)', subtitle: 'Declining earnings' },
      { value: 'low', label: 'Low (0-10%)', subtitle: 'Slow growth' },
      { value: 'medium', label: 'Medium (10-25%)', subtitle: 'Moderate growth' },
      { value: 'high', label: 'High (25-50%)', subtitle: 'Strong growth' },
      { value: 'very_high', label: 'Very High (50%+)', subtitle: 'Exceptional growth' },
    ],
    icon: TrendingUp,
  },
];

// Finviz Technical Filters
export const finvizTechnicalFilters: FilterItem[] = [
  {
    id: 'finviz_rsi_min',
    type: 'number',
    label: 'RSI (14) Min',
    shortDescription: 'Minimum RSI value',
    description: 'Minimum Relative Strength Index (14-day).',
    defaultValue: undefined,
    min: 0,
    max: 100,
    step: 1,
    prefix: '',
    suffix: '',
    icon: Activity,
  },
  {
    id: 'finviz_rsi_max',
    type: 'number',
    label: 'RSI (14) Max',
    shortDescription: 'Maximum RSI value',
    description: 'Maximum Relative Strength Index (14-day).',
    defaultValue: undefined,
    min: 0,
    max: 100,
    step: 1,
    prefix: '',
    suffix: '',
    icon: Activity,
  },
  {
    id: 'finviz_rel_volume',
    type: 'select',
    label: 'Relative Volume',
    shortDescription: 'Relative volume filter',
    description: 'Filter by relative volume compared to average.',
    defaultValue: '',
    options: [
      { value: '', label: 'Any', subtitle: 'No filter' },
      { value: '1x', label: 'Over 1x', subtitle: 'Above average volume' },
      { value: '2x', label: 'Over 2x', subtitle: 'Double average volume' },
      { value: '3x', label: 'Over 3x', subtitle: 'Triple average volume' },
    ],
    icon: Volume2,
  },
  {
    id: 'finviz_price_min',
    type: 'number',
    label: 'Price Min',
    shortDescription: 'Minimum stock price',
    description: 'Minimum stock price in dollars.',
    defaultValue: undefined,
    min: 0,
    max: 100000,
    step: 1,
    prefix: '$',
    suffix: '',
    icon: DollarSign,
  },
  {
    id: 'finviz_price_max',
    type: 'number',
    label: 'Price Max',
    shortDescription: 'Maximum stock price',
    description: 'Maximum stock price in dollars.',
    defaultValue: undefined,
    min: 0,
    max: 100000,
    step: 1,
    prefix: '$',
    suffix: '',
    icon: DollarSign,
  },
  {
    id: 'finviz_avg_volume',
    type: 'select',
    label: 'Average Volume',
    shortDescription: 'Average volume filter',
    description: 'Filter by average daily volume.',
    defaultValue: '',
    options: [
      { value: '', label: 'Any', subtitle: 'No filter' },
      { value: '100k', label: 'Over 100K', subtitle: 'Above 100,000 shares' },
      { value: '500k', label: 'Over 500K', subtitle: 'Above 500,000 shares' },
      { value: '1m', label: 'Over 1M', subtitle: 'Above 1,000,000 shares' },
      { value: '5m', label: 'Over 5M', subtitle: 'Above 5,000,000 shares' },
    ],
    icon: Volume2,
  },
  {
    id: 'finviz_short_float_min',
    type: 'number',
    label: 'Short Float Min',
    shortDescription: 'Minimum short interest %',
    description: 'Minimum short interest as percentage of float.',
    defaultValue: undefined,
    min: 0,
    max: 100,
    step: 0.1,
    prefix: '',
    suffix: '%',
    icon: Percent,
  },
  {
    id: 'finviz_short_float_max',
    type: 'number',
    label: 'Short Float Max',
    shortDescription: 'Maximum short interest %',
    description: 'Maximum short interest as percentage of float.',
    defaultValue: undefined,
    min: 0,
    max: 100,
    step: 0.1,
    prefix: '',
    suffix: '%',
    icon: Percent,
  },
  {
    id: 'finviz_beta_min',
    type: 'number',
    label: 'Beta Min',
    shortDescription: 'Minimum beta',
    description: 'Minimum beta (volatility vs market).',
    defaultValue: undefined,
    min: 0,
    max: 5,
    step: 0.1,
    prefix: '',
    suffix: '',
    icon: Activity,
  },
  {
    id: 'finviz_beta_max',
    type: 'number',
    label: 'Beta Max',
    shortDescription: 'Maximum beta',
    description: 'Maximum beta (volatility vs market).',
    defaultValue: undefined,
    min: 0,
    max: 5,
    step: 0.1,
    prefix: '',
    suffix: '',
    icon: Activity,
  },
  {
    id: 'finviz_sma_vs_price',
    type: 'card-checkbox-group',
    label: 'Price vs SMA',
    shortDescription: 'Price relative to moving averages',
    description: 'Filter by price position relative to simple moving averages.',
    defaultValue: [],
    hasSelectAll: true,
    options: [
      { value: 'SMA20_ABOVE', label: 'Price vs SMA20', subtitle: 'Above/below 20-day MA' },
      { value: 'SMA50_ABOVE', label: 'Price vs SMA50', subtitle: 'Above/below 50-day MA' },
      { value: 'SMA200_ABOVE', label: 'Price vs SMA200', subtitle: 'Above/below 200-day MA' },
    ],
    icon: LineChart,
  },
];

// Finviz ETF Filters
export const finvizETFFilters: FilterItem[] = [
  {
    id: 'finviz_etf_tracks_index',
    type: 'select',
    label: 'Tracks Index',
    shortDescription: 'Index the ETF tracks',
    description: 'Filter ETFs by the index they track.',
    defaultValue: '',
    options: [
      { value: '', label: 'Any', subtitle: 'No filter' },
      { value: 'SP500', label: 'S&P 500', subtitle: 'Tracks S&P 500' },
      { value: 'NASDAQ', label: 'NASDAQ 100', subtitle: 'Tracks NASDAQ 100' },
      { value: 'DOW', label: 'Dow Jones', subtitle: 'Tracks Dow Jones Industrial' },
      { value: 'RUSSELL', label: 'Russell 2000', subtitle: 'Tracks Russell 2000' },
    ],
    icon: BarChart2,
  },
  {
    id: 'finviz_etf_avg_volume_min',
    type: 'number',
    label: 'Average Volume Min',
    shortDescription: 'Minimum average volume',
    description: 'Minimum average daily trading volume.',
    defaultValue: undefined,
    min: 0,
    max: 10000000,
    step: 10000,
    prefix: '',
    suffix: '',
    icon: Volume2,
  },
  {
    id: 'finviz_etf_expense_min',
    type: 'number',
    label: 'Expense Ratio Min',
    shortDescription: 'Maximum expense ratio %',
    description: 'Maximum annual expense ratio percentage.',
    defaultValue: undefined,
    min: 0,
    max: 5,
    step: 0.01,
    prefix: '',
    suffix: '%',
    icon: Percent,
  },
  {
    id: 'finviz_etf_expense_max',
    type: 'number',
    label: 'Expense Ratio Max',
    shortDescription: 'Maximum expense ratio %',
    description: 'Maximum annual expense ratio percentage.',
    defaultValue: undefined,
    min: 0,
    max: 5,
    step: 0.01,
    prefix: '',
    suffix: '%',
    icon: Percent,
  },
];

// All Finviz filters combined
export const finvizFilters: FilterItem[] = [
  ...finvizDescriptiveFilters,
  ...finvizFundamentalFilters,
  ...finvizTechnicalFilters,
  ...finvizETFFilters,
];
